import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as filterGroupSelectors from 'data/filterGroup/filterGroup.selectors';
import * as filterGroupActions from 'data/filterGroup/filterGroup.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FilterGroup } from 'data/filterGroup/filterGroup.types';
import FilterGroupForm from 'routes/FilterGroup/components/FilterGroupForm';
import * as types from 'data/filterGroup/filterGroup.types';
import { filterGroupTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFilterGroupTemplate,
} from '../../utils/filterGroupTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.FilterGroup
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  filterGroupTemplateDef: adaptTemplateVisibilityMode(filterGroupTemplate, 'edit'),
  selectedFilterGroup: filterGroupSelectors
    .getSelectedFilterGroup(state)[0],
  originalSelectedFilterGroup: filterGroupSelectors
    .getSelectedFilterGroup(state)[0],
  filterGroup: filterGroupSelectors.getFilterGroup(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToFilterGroupList: filterGroupActions.goToFilterGroupList,
  updateFilterGroup: filterGroupActions.updateFilterGroup,
  getFilterGroup: filterGroupActions.getFilterGroup,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const FilterGroupEdit = ({
  history,
  selectedFilterGroup,
  originalSelectedFilterGroup,
  filterGroupTemplateDef,
  filterGroup,
  goToFilterGroupList,
  updateFilterGroup,
  getFilterGroup,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<FilterGroup>>({});
  const [template, setTemplate] = useState(filterGroupTemplateDef);
  const { t } = useTranslation();

  const id = selectedFilterGroup?.id;

  useEffect(() => {
    if (id) {
      getFilterGroup(id);
    }
  }, [id, getFilterGroup]);

  useEffect(() => {
    if (filterGroup != null && template != null) {
      const clonedFilterGroup = cloneDeep(filterGroup);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      setTemplate(clonedTemplate);
      setFormData(clonedFilterGroup);
    }
  }, [filterGroup, id]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const dialedFilterGroupData = formRef.current.getValues();
    const payload = {
      data: dialedFilterGroupData,
      originalData: {
        fgId: originalSelectedFilterGroup?.fgId || '',
        name: originalSelectedFilterGroup?.name || ''
      }
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload && filterGroup != null) {
      updateFilterGroup(filterGroup?.id, payload, history);
    }
  };

  const handleCancel = () => goToFilterGroupList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as FilterGroup);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptFilterGroupTemplate(clonedTemplate);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <FilterGroupForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (filterGroup == null) {
    return <Fragment />;
  }

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('filterGroup.edit.title'))}
        {renderForm()}
      </FormLayoutContent>
      <FormLayoutFooter>
        {renderFormPanel('')}
      </FormLayoutFooter>
    </FormLayout>
  );
};

export default withRouter(connector(FilterGroupEdit));
